import {action, _, React} from '@api'
import {ICatalogItemDetailsPathElement} from '@design-board'
import {IJsonTabNode, MoveTabDirection, TabNode, TabSetNode} from '@flexlayout'
import {IFlexLayoutPdfTabConfig} from '@modules/pdf'
import {globalStores} from '@stores'
import {CatalogViewMode, DEFAULT_CATALOG_USER_SETTINGS, FlexLayoutStore, IFlexLayoutInlineCatalogItemDetailsConfig} from '@stores/catalog'
import {IFlexLayoutCatalogRootConfig, C8CreateFlexLayoutStore, IC8TabConfig} from '@stores/catalog/flexLayout'
import {AssortmentConfig, IWorkspacePreset, WorkspaceConfig} from '@stores/config'
import {guid} from '@utils'
import {toJS, when} from 'mobx'

export interface CreateTabViewProps {
  action: 'new' | 'update',
  tab?: TabNode,
  tabSet: TabSetNode,
  tabJson: IJsonTabNode
  direction?: MoveTabDirection
}

export class CreateFlexLayoutStore {
  constructor(public store: FlexLayoutStore) {
    this.c8 = new C8CreateFlexLayoutStore(store)
  }

  c8: C8CreateFlexLayoutStore

  @action onNewWorkspaceItem = (e: React.FormEvent, v: { workspace: WorkspaceConfig }) => {
    const {store: {maximizeTab, model: {firstTabSet}}, newCatalogTab} = this
    DEBUG && console.debug(`Create new tab and maximize - ${v.workspace._id}`, v)

    if (firstTabSet) {
      const tab = newCatalogTab({view: 'card', parentTabSet: firstTabSet, selectNewTab: true, ...v})
      tab && maximizeTab(tab.id)
    } else {
      DEBUG && console.debug(`Unable to find tabset in which to create new maximized tab.`)
    }
  }

  @action private _addTabToCatalogMDI = (tab: IJsonTabNode, parentTabset?: TabSetNode, autoSelect = false): TabNode => {
    const {store: {model}} = this

    if (tab) {
      let tabSet: TabSetNode = parentTabset && !parentTabset.isLayoutSidebar
                               ? parentTabset
                               : ((model.nodesByType.tabset ?? []).find(ts => ts.children.some((c: TabNode) => c.component == 'catalog')) ?? model?.activeTabset)

      if (tabSet?.isLayoutSidebar) {
        tabSet = (model.nodesByType.tabset ?? []).find(ts => !ts.isLayoutSidebar)
      }

      // const tabsetJson = tabset?.json

      console.debug(`_addTabToCatalogMDI`, {tab, parentTabset, autoSelect}) //, toJS({tabsetJson: tabSet?.json, tab}, {recurseEverything: true}), {tabSet})
      // this.
      if (tabSet) {
        const r = TabNode._fromJson(tab, model, true)
        const pos = tabSet.addChild(r)
        autoSelect && tabSet._setSelected(pos)

        model._changeListener && model._changeListener()
        return r
        // tabsetJson.children.push(tab)
        // tabsetJson.selected = tabsetJson.children.length - 1
      } else {
        const newTabSet = TabSetNode._fromJson({
          type: 'tabset',
          weight: model.root.averageWeight,
          children: [tab]
        }, model)
        model.root.addChild(newTabSet)

        return newTabSet.tabs[0]

        // modelJson.layout.children.push({
        //   type: 'tabset',
        //   children: [tab]
        // })
      }
    }
    return null
  }

  @action newCatalogTab = ({view, parentTabSet, config, selectNewTab, workspace, location}: { view?: CatalogViewMode, location?: MoveTabDirection, config?: Partial<IFlexLayoutCatalogRootConfig>, parentTabSet?: TabSetNode, selectNewTab?: boolean, workspace?: Partial<WorkspaceConfig> }) => {
    DEBUG && console.debug(`newCatalogTab`, {view, parentTabSet, workspace, selectNewTab})

    const {store, store: {model, presetsStore, workspaces: {defaultCatalogUserSettings}}} = this
    let tabJson: IJsonTabNode = {id: guid()}
    switch (view ?? 'card') {
      case 'card':
        _.merge(tabJson, presetsStore.cardTab)
        break
      case 'report':
        break
      case 'table':
        _.merge(tabJson, presetsStore.tableTab)
        break
      case 'pdf':
        _.merge(tabJson, presetsStore.pdfTab)
        break
    }

    const tabConfig = tabJson.config as IFlexLayoutCatalogRootConfig,
      ws = workspace ?? this.store.catalog?.workspace?.config ?? this.store?.activeCatalogRoot?.catalog?.workspace

    if (!ws) throw new Error(`Unable to determine workspace`)

    tabJson.name = ws.title
    tabConfig.settings.workspace = ws._id

    _.merge(tabConfig.settings.catalog, _.cloneDeep(defaultCatalogUserSettings), _.cloneDeep(workspace.catalog.defaultUserSettings))

    this._addTabToCatalogMDI(tabJson, parentTabSet, selectNewTab)

    const tab = model.nodeById<TabNode<IFlexLayoutCatalogRootConfig>>(tabJson?.id)

    if (config) {
      _.merge(tab.attributes, {config})
    }

    when(() => tab.catalogRoot?.catalog?.cv != null && tab.catalogRoot?.catalog?.hasLoadedConfig, () => {
      console.debug(`Resetting ${tab.id} catalog to default view options`)
      tab.catalogRoot.catalog.cv.resetCardViewToDefault()
    })

    if (location) {
      store.moveTab(tab, location)
    }

    return tab
  }

  @action newC8Tab = (props: any, parentTabset?: TabSetNode) => {
    this._addTabToCatalogMDI({
      component: 'c8',
      config: props,
      name: 'C8',
      type: 'tab',
      id: this.store.newId()
    }, parentTabset)
  }

  @action onNewOrUpdateTabFromWorkspaceItem = (e, {preset, assortment, tabSet, tab, ...props}: { workspace?: WorkspaceConfig, preset?: IWorkspacePreset, assortment?: AssortmentConfig, tabSet?: TabSetNode, tab?: TabNode }) => {
    const {store: {stores: {workspaces: {defaultCatalogUserSettings, availableWorkspaces}}, presetsStore}} = this,
      presetId = !preset ? null : tab?.catalogRoot?.storesToInject?.catalog?.settings?.preset === preset._id ? null : preset?._id,
      workspace = props.workspace ?? _.first(availableWorkspaces)

    DEBUG && console.debug(`onNewOrUpdateTabFromWorkspaceItem`, {action, workspace: toJS(workspace), assortment: toJS(assortment), preset: toJS(preset), tab, tabSet})

    let newCatalogConfig = _.cloneDeep({
      config: {
        settings: {
          workspace: workspace?._id,
          catalog: _.merge(
            tab ? {} : _.merge({}, defaultCatalogUserSettings, workspace.catalog?.defaultUserSettings ?? {}), // Use config side overrides if new tab
            {
              preset: presetId,
              assortment: {
                id: assortment?._id,
                orders: {},
                layout: _.merge({}, DEFAULT_CATALOG_USER_SETTINGS.assortment.layout, assortment?.layout)
              }
            }
          )
        }
      } as IFlexLayoutCatalogRootConfig
    })

    if (assortment) {
      _.merge(newCatalogConfig, {
        config: {
          settings: {catalog: {groupBy: {heroCards: {show: true}}, align: 'center'}}
        } as Partial<IFlexLayoutCatalogRootConfig>
      })
    }

    if (tab) { // Existing tab is switching to a catalog/workspace/preset
      tab.attributes.component = 'catalog'
      const config = tab._config as IFlexLayoutCatalogRootConfig
      config.settings = newCatalogConfig.config.settings
      _.assign(tab.attributes, newCatalogConfig as Partial<IJsonTabNode>)

      when(
        () => tab.catalogRoot?.storesToInject?.catalog?.hasLoadedConfig,
        () => {
          const {catalogRoot: {storesToInject: {catalog}}} = tab

          if (catalog) {
            _.mergeWith(catalog.settings, newCatalogConfig.config.settings.catalog, (a, b) => b === null || b === undefined ? null : undefined)
            if (presetId) {
              catalog.presets.loadPreset(workspace, preset, true)
            } else {
              catalog.cv?.resetCardViewToDefault()
            }

            tab.model.onChange()
          }
        })

      tab.selectInTabSet()

      tab.model.onChange()
    } else { // New workspace tab
      const tabJson = _.merge({}, presetsStore.cardTab, newCatalogConfig)

      const tab = this._addTabToCatalogMDI(tabJson, tabSet, true)
      if (tab?.component === 'catalog') {
        when(() => tab.catalogRoot?.storesToInject?.catalog?.hasLoadedConfig, () => {
          const {catalogRoot: {storesToInject: {catalog}}} = tab
          if (presetId) {
            catalog.presets.loadPreset(workspace, preset, true)
          } else {
            catalog.cv?.resetCardViewToDefault()
          }
        })
      }
    }
  }

  @action createPdfTab = ({tab, tabSet, config}: { tab?: TabNode, tabSet?: TabSetNode, config?: Partial<IFlexLayoutPdfTabConfig> } = {}) => {
    const {createOrUpdateTabView, store: {strings}} = this

    const newTab: IJsonTabNode = {
      type: 'tab',
      component: 'pdfViewer',
      name: strings.pdfView,
      config: _.merge(
        {
          pdfSettings: {
            selectedArtifact: null
          }
        } as IFlexLayoutPdfTabConfig,
        config)
    }

    if (tab?.catalogRoot) {
      return tab.catalogRoot.addNewChildTab(newTab)
    } else {
      return createOrUpdateTabView({tabSet: tabSet ?? tab?.tabSet, tabJson: newTab, action: tab ? 'update' : 'new', tab})
    }
  }

  @action createOrUpdateTabView = ({action, tabJson, tab, direction, ...props}: CreateTabViewProps): TabNode => {
    const {store: {model, newId, layout, moveTab}} = this
    let tabSet = props.tabSet ?? model?.activeTabset ?? model?.firstTabSet

    DEBUG && console.debug(`createOrUpdateTabView`, {action, tabJson, tab, tabSet, direction})

    if (action === 'new') {
      tab = tabSet.addChildJson({...tabJson, id: newId()}, true)
    } else if (action === 'update') {
      tab.attributes.component = tabJson.component
      _.merge(tab._config, tabJson.config)
      layout?.store?.asyncDoLayout()
    } else {
      throw new Error(`Must specify action 'create' or 'update' to this method`)
    }

    direction && moveTab(tab, direction)

    return tab
  }

  @action newC8Tab_CollectionTargets = ({parent, action}: { parent: TabNode<any>, action?: 'new' | 'update' }) => {
    const {createOrUpdateTabView, store: {presetsStore: {c8Tab}}} = this
    createOrUpdateTabView({
      tabSet: parent.tabSet, action: action ?? 'new', direction: 'right',
      tabJson: {
        ...c8Tab,
        name: 'Collection Targets',
        config: {
          settings: {
            subComponent: 'collection-targets',
            //queryString: '#URL=C387650&Tab=Products&NR=1',
            parentTabId: parent?.id
          }
        } as Partial<IC8TabConfig>
      }
    })
  }

  @action createItemDetailsTab = ({path: detailsPath, name, parentTab, selectedPathIndex, selectNewTab, ...params}: { tabSet?: TabSetNode, selectNewTab?: boolean, indexInParent?: number, selectedPathIndex?: number, path: Array<ICatalogItemDetailsPathElement>; workspace?: string; name?: string; parentTab?: string }) => {
    const {store: {stores: {workspaces}}} = this,
      workspace = params.workspace ?? this.store.stores.workspaces.byIndexScalar[_.first(detailsPath)?.esIndex]?._id,
      ws = workspaces.workspaceFor(workspace)

    const newTabJson = {
        name,
        type: 'tab',
        enableRenderOnDemand: false, // Force it to stay loaded so the user can quickly switch between them
        component: 'inlineCatalogItemDetails',
        id: guid(),
        config: {
          settings: {
            workspace,
            detailsPath,
            selectedPathIndex: selectedPathIndex ?? 0,
            parentTab,
            expanded: false,
            orientation: ws?.config?.catalog?.defaultUserSettings?.details?.modal?.orientation ?? 'horz'
          }
        } as IFlexLayoutInlineCatalogItemDetailsConfig
      } as IJsonTabNode<IFlexLayoutInlineCatalogItemDetailsConfig>,
      tabSet = params.tabSet ?? this.store.activeTab.tabSet

    return tabSet.addChildJson(newTabJson, selectNewTab ?? false, params.indexInParent ?? tabSet.tabs.length)
  }
}


// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexComponents/CreateFlexLayoutStore.tsx